import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { 
  Plus, 
  Edit, 
  Trash2, 
  Search,
  Building,
  Mail,
  Phone,
  Globe,
  CreditCard
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin`;

const AdminClients = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    name: "",
    company: "",
    address: "",
    country: "",
    email: "",
    phone: "",
    tax_code: "",
    contact_person: "",
    payment_method: "bank",
    payment_details: {
      bank_name: "",
      bank_address: "",
      account_number: "",
      account_type: "",
      routing_number: "",
      swift_code: "",
      beneficiary_name: "",
      payoneer_email: "",
      paypal_email: ""
    }
  });

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const token = localStorage.getItem("admin_token");
      const response = await axios.get(`${API}/clients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClients(response.data);
    } catch (error) {
      console.error("Error fetching clients:", error);
      toast.error("Failed to load clients");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setSelectedClient(null);
    setFormData({
      name: "",
      company: "",
      address: "",
      country: "",
      email: "",
      phone: "",
      tax_code: "",
      contact_person: "",
      payment_method: "bank",
      payment_details: {
        bank_name: "",
        bank_address: "",
        account_number: "",
        account_type: "",
        routing_number: "",
        swift_code: "",
        beneficiary_name: "",
        payoneer_email: "",
        paypal_email: ""
      }
    });
    setIsModalOpen(true);
  };

  const handleEdit = (client) => {
    setSelectedClient(client);
    setFormData({
      name: client.name || "",
      company: client.company || "",
      address: client.address || "",
      country: client.country || "",
      email: client.email || "",
      phone: client.phone || "",
      tax_code: client.tax_code || "",
      contact_person: client.contact_person || "",
      payment_method: client.payment_method || "bank",
      payment_details: client.payment_details || {}
    });
    setIsModalOpen(true);
  };

  const handleDelete = (client) => {
    setSelectedClient(client);
    setIsDeleteOpen(true);
  };

  const confirmDelete = async () => {
    try {
      const token = localStorage.getItem("admin_token");
      await axios.delete(`${API}/clients/${selectedClient.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Client deleted successfully");
      fetchClients();
    } catch (error) {
      console.error("Error deleting client:", error);
      toast.error("Failed to delete client");
    } finally {
      setIsDeleteOpen(false);
      setSelectedClient(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.company || !formData.email) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);

    try {
      const token = localStorage.getItem("admin_token");

      if (selectedClient) {
        await axios.put(`${API}/clients/${selectedClient.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Client updated successfully");
      } else {
        await axios.post(`${API}/clients`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Client created successfully");
      }
      
      setIsModalOpen(false);
      fetchClients();
    } catch (error) {
      console.error("Error saving client:", error);
      toast.error("Failed to save client");
    } finally {
      setIsSubmitting(false);
    }
  };

  const filteredClients = clients.filter(client => 
    client.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.company?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getPaymentMethodBadge = (method) => {
    const colors = {
      bank: "bg-blue-100 text-blue-800",
      payoneer: "bg-orange-100 text-orange-800",
      paypal: "bg-indigo-100 text-indigo-800"
    };
    return colors[method] || "bg-slate-100 text-slate-800";
  };

  if (loading) {
    return (
      <div className="p-6 lg:p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-slate-200 rounded w-48"></div>
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-24 bg-slate-200 rounded-xl"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-slate-900">Clients</h1>
          <p className="text-slate-600 mt-1">Manage your client database for invoicing</p>
        </div>
        <Button 
          onClick={handleCreate}
          data-testid="create-client-btn"
          className="bg-[#1e3a8a] hover:bg-[#172554]"
        >
          <Plus className="w-4 h-4 mr-2" /> Add New Client
        </Button>
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
        <Input
          placeholder="Search clients..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
          data-testid="search-clients-input"
        />
      </div>

      {/* Clients List */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredClients.length === 0 ? (
          <Card className="border-0 shadow-sm md:col-span-2 lg:col-span-3">
            <CardContent className="p-12 text-center">
              <Building className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No clients found</p>
              <Button onClick={handleCreate} variant="outline" className="mt-4">
                Add your first client
              </Button>
            </CardContent>
          </Card>
        ) : (
          filteredClients.map((client, index) => (
            <motion.div
              key={client.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <Card className="border-0 shadow-sm hover:shadow-md transition-shadow h-full">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-12 h-12 bg-[#1e3a8a] rounded-xl flex items-center justify-center">
                      <span className="text-white font-bold text-lg">
                        {client.company?.charAt(0) || "C"}
                      </span>
                    </div>
                    <Badge className={getPaymentMethodBadge(client.payment_method)}>
                      {client.payment_method}
                    </Badge>
                  </div>
                  
                  <h3 className="text-lg font-semibold text-slate-900 mb-1">{client.company}</h3>
                  <p className="text-sm text-slate-500 mb-4">{client.name}</p>
                  
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2 text-slate-600">
                      <Mail className="w-4 h-4 text-slate-400" />
                      <span className="truncate">{client.email}</span>
                    </div>
                    {client.phone && (
                      <div className="flex items-center gap-2 text-slate-600">
                        <Phone className="w-4 h-4 text-slate-400" />
                        <span>{client.phone}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-slate-600">
                      <Globe className="w-4 h-4 text-slate-400" />
                      <span>{client.country}</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2 mt-4 pt-4 border-t">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleEdit(client)}
                      className="flex-1"
                      data-testid={`edit-client-${client.id}`}
                    >
                      <Edit className="w-4 h-4 mr-1" /> Edit
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleDelete(client)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      data-testid={`delete-client-${client.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))
        )}
      </div>

      {/* Create/Edit Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedClient ? "Edit Client" : "Add New Client"}</DialogTitle>
            <DialogDescription>
              {selectedClient ? "Update client details" : "Add a new client for invoicing"}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-6 py-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="company">Company Name *</Label>
                <Input
                  id="company"
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  placeholder="e.g. Ray Advertising LLC"
                  required
                  data-testid="client-company-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="name">Contact Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g. David Garcia"
                  required
                  data-testid="client-name-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="address">Address</Label>
              <Input
                id="address"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="e.g. 1267, Willis St, STE 200, Redding, CA 98001"
                data-testid="client-address-input"
              />
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="country">Country *</Label>
                <Input
                  id="country"
                  value={formData.country}
                  onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                  placeholder="e.g. USA"
                  required
                  data-testid="client-country-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="tax_code">Tax Code / GST</Label>
                <Input
                  id="tax_code"
                  value={formData.tax_code}
                  onChange={(e) => setFormData({ ...formData, tax_code: e.target.value })}
                  placeholder="e.g. 0402160074"
                  data-testid="client-tax-input"
                />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="e.g. billing@company.com"
                  required
                  data-testid="client-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="e.g. +1 234 567 8900"
                  data-testid="client-phone-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="contact_person">Additional Contact Person</Label>
              <Input
                id="contact_person"
                value={formData.contact_person}
                onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                placeholder="e.g. Sarah (Accounts)"
                data-testid="client-contact-input"
              />
            </div>

            {/* Payment Method */}
            <div className="space-y-4 p-4 bg-slate-50 rounded-lg">
              <div className="space-y-2">
                <Label>Payment Method</Label>
                <Select 
                  value={formData.payment_method} 
                  onValueChange={(value) => setFormData({ ...formData, payment_method: value })}
                >
                  <SelectTrigger data-testid="payment-method-select">
                    <SelectValue placeholder="Select payment method" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bank">Bank Transfer (ACH)</SelectItem>
                    <SelectItem value="payoneer">Payoneer</SelectItem>
                    <SelectItem value="paypal">PayPal</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {formData.payment_method === "bank" && (
                <div className="grid sm:grid-cols-2 gap-4 mt-4">
                  <div className="space-y-2">
                    <Label>Bank Name</Label>
                    <Input
                      value={formData.payment_details.bank_name || ""}
                      onChange={(e) => setFormData({
                        ...formData,
                        payment_details: { ...formData.payment_details, bank_name: e.target.value }
                      })}
                      placeholder="e.g. Citibank"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Bank Address</Label>
                    <Input
                      value={formData.payment_details.bank_address || ""}
                      onChange={(e) => setFormData({
                        ...formData,
                        payment_details: { ...formData.payment_details, bank_address: e.target.value }
                      })}
                      placeholder="e.g. 111 Wall Street, New York, NY 10043, USA"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Account Number</Label>
                    <Input
                      value={formData.payment_details.account_number || ""}
                      onChange={(e) => setFormData({
                        ...formData,
                        payment_details: { ...formData.payment_details, account_number: e.target.value }
                      })}
                      placeholder="Account number"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Account Type</Label>
                    <Select 
                      value={formData.payment_details.account_type || ""} 
                      onValueChange={(value) => setFormData({
                        ...formData,
                        payment_details: { ...formData.payment_details, account_type: value }
                      })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select account type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="CHECKING">Checking</SelectItem>
                        <SelectItem value="SAVINGS">Savings</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Routing Number (ABA)</Label>
                    <Input
                      value={formData.payment_details.routing_number || ""}
                      onChange={(e) => setFormData({
                        ...formData,
                        payment_details: { ...formData.payment_details, routing_number: e.target.value }
                      })}
                      placeholder="Routing number"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>SWIFT Code</Label>
                    <Input
                      value={formData.payment_details.swift_code || ""}
                      onChange={(e) => setFormData({
                        ...formData,
                        payment_details: { ...formData.payment_details, swift_code: e.target.value }
                      })}
                      placeholder="SWIFT code"
                    />
                  </div>
                  <div className="sm:col-span-2 space-y-2">
                    <Label>Beneficiary Name</Label>
                    <Input
                      value={formData.payment_details.beneficiary_name || ""}
                      onChange={(e) => setFormData({
                        ...formData,
                        payment_details: { ...formData.payment_details, beneficiary_name: e.target.value }
                      })}
                      placeholder="Beneficiary name"
                    />
                  </div>
                </div>
              )}

              {formData.payment_method === "payoneer" && (
                <div className="space-y-2 mt-4">
                  <Label>Payoneer Email</Label>
                  <Input
                    type="email"
                    value={formData.payment_details.payoneer_email || ""}
                    onChange={(e) => setFormData({
                      ...formData,
                      payment_details: { ...formData.payment_details, payoneer_email: e.target.value }
                    })}
                    placeholder="payoneer@email.com"
                  />
                </div>
              )}

              {formData.payment_method === "paypal" && (
                <div className="space-y-2 mt-4">
                  <Label>PayPal Email</Label>
                  <Input
                    type="email"
                    value={formData.payment_details.paypal_email || ""}
                    onChange={(e) => setFormData({
                      ...formData,
                      payment_details: { ...formData.payment_details, paypal_email: e.target.value }
                    })}
                    placeholder="paypal@email.com"
                  />
                </div>
              )}
            </div>

            <div className="flex gap-4 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsModalOpen(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 bg-[#1e3a8a] hover:bg-[#172554]"
                data-testid="save-client-btn"
              >
                {isSubmitting ? "Saving..." : selectedClient ? "Update Client" : "Add Client"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Client?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete "{selectedClient?.company}". This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-client"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminClients;
